import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBranding } from '@/contexts/BrandingContext';
import { ForcePasswordChange } from '@/components/ForcePasswordChange';
import { useDeviceType } from '@/hooks/useDeviceType';
import { QuickActionsPanel } from '@/components/QuickActionsPanel';
import { MobileVoiceButton } from '@/components/MobileVoiceButton';
import { SceneShortcuts } from '@/components/SceneShortcuts';
import ThemeSwitcher from '@/components/ThemeSwitcher';
import ThemeSyncer from '@/components/ThemeSyncer';
import {
  House, UsersThree, CurrencyDollar, CalendarBlank,
  Gear, Bell, ChatCircleDots, SignOut, List, X,
  CookingPot, FirstAid, UserList, ShoppingCart, FilePdf,
  ChatTeardrop, Lightning, WifiHigh, FolderLock, Wrench, Car, AddressBook, HouseLine,
  VideoCamera, Package, Users, Bank, Wallet, ChartBar, Megaphone, ShieldCheck, DeviceMobile, Receipt, Robot, ShieldWarning,
  Compass, MapTrifold, Brain, SlidersHorizontal, UserFocus,
  DotsThreeOutline, SquaresFour, Confetti, Airplane, Baby, SunHorizon, CaretDown
} from '@phosphor-icons/react';

const navItemsDef = [
  { to: '/dashboard', labelKey: 'dashboard', icon: House },
  { to: '/control-center', labelKey: 'control_center', icon: SlidersHorizontal },
  { to: '/home-layout', labelKey: 'home_layout', icon: MapTrifold },
  { to: '/room-automation', labelKey: 'room_automation', icon: Lightning },
  { to: '/ai-intelligence', labelKey: 'ai_intelligence', icon: Brain },
  { to: '/face-recognition', labelKey: 'face_recognition', icon: UserFocus },
  { to: '/staff', labelKey: 'staff', icon: UsersThree },
  { to: '/cashbook', labelKey: 'cashbook', icon: Bank },
  { to: '/expenses', labelKey: 'expenses', icon: CurrencyDollar },
  { to: '/calendar', labelKey: 'calendar', icon: CalendarBlank },
  { to: '/trips', labelKey: 'trips', defaultLabel: 'Trips', icon: Airplane },
  { to: '/meals', labelKey: 'meals', icon: CookingPot },
  { to: '/grocery', labelKey: 'grocery', icon: ShoppingCart },
  { to: '/health', labelKey: 'health', icon: FirstAid },
  { to: '/kids', labelKey: 'kids_zone', defaultLabel: 'Kids Zone', icon: Baby },
  { to: '/weekend', labelKey: 'weekend', defaultLabel: 'Weekend', icon: SunHorizon },
  { to: '/visitors', labelKey: 'visitors', icon: UserList },
  { to: '/members', labelKey: 'members', icon: Users },
  { to: '/security', labelKey: 'security', icon: ShieldWarning },
  { to: '/appliances', labelKey: 'appliances', icon: Wrench },
  { to: '/energy', labelKey: 'energy', icon: Lightning },
  { to: '/wifi', labelKey: 'wifi', icon: WifiHigh },
  { to: '/family-chat', labelKey: 'family_chat', icon: ChatTeardrop },
  { to: '/documents', labelKey: 'documents', icon: FolderLock },
  { to: '/providers', labelKey: 'providers', icon: AddressBook },
  { to: '/maintenance', labelKey: 'maintenance', icon: Wrench },
  { to: '/vehicles', labelKey: 'vehicles', icon: Car },
  { to: '/inventory', labelKey: 'inventory', icon: Package },
  { to: '/smart-home', labelKey: 'smart_home', icon: HouseLine },
  { to: '/cameras', labelKey: 'cameras', icon: VideoCamera },
  { to: '/loans', labelKey: 'loans', icon: Bank },
  { to: '/personal-expenses', labelKey: 'personal_expenses', icon: Wallet },
  { to: '/analytics', labelKey: 'analytics', icon: ChartBar },
  { to: '/announcements', labelKey: 'announcements', icon: Megaphone },
  { to: '/audit-log', labelKey: 'audit_log', icon: ShieldCheck },
  { to: '/devices', labelKey: 'devices', icon: DeviceMobile },
  { to: '/staff-analytics', labelKey: 'staff_analytics', icon: ChartBar },
  { to: '/receipts', labelKey: 'receipts', icon: Receipt },
  { to: '/automation', labelKey: 'automation', icon: Robot },
  { to: '/alert-center', labelKey: 'alert_center', icon: Bell },
  { to: '/events', labelKey: 'events', icon: Confetti },
  { to: '/reports', labelKey: 'reports', icon: FilePdf },
  { to: '/notifications', labelKey: 'alerts', icon: Bell },
  { to: '/ai-chat', labelKey: 'ai_chat', icon: ChatCircleDots },
  { to: '/settings', labelKey: 'settings', icon: Gear },
];

// Sidebar grouping — keeps the long menu tidy with collapsible sections.
const NAV_TOP = ['/dashboard', '/control-center'];
const NAV_GROUPS = [
  { id: 'home', label: 'Home & Security', paths: ['/home-layout', '/room-automation', '/smart-home', '/automation', '/energy', '/appliances', '/wifi', '/devices', '/cameras', '/security', '/face-recognition', '/alert-center'] },
  { id: 'family', label: 'Family', paths: ['/calendar', '/events', '/trips', '/weekend', '/kids', '/health', '/members', '/family-chat', '/announcements'] },
  { id: 'kitchen', label: 'Kitchen', paths: ['/meals', '/grocery', '/inventory'] },
  { id: 'money', label: 'Money', paths: ['/expenses', '/cashbook', '/personal-expenses', '/loans', '/receipts', '/analytics', '/reports'] },
  { id: 'people', label: 'People & Services', paths: ['/staff', '/staff-analytics', '/visitors', '/providers', '/vehicles', '/maintenance', '/documents'] },
  { id: 'system', label: 'AI & System', paths: ['/ai-intelligence', '/ai-chat', '/audit-log', '/notifications'] },
];
const NAV_BOTTOM = ['/settings'];

const mobileNavDef = [
  { to: '/dashboard', labelKey: 'dashboard', icon: House },
  { to: '/control-center', labelKey: 'control', icon: SlidersHorizontal },
  { to: '/room-automation', labelKey: 'rooms', icon: Lightning },
  { to: '/alert-center', labelKey: 'alerts', icon: Bell },
  { id: 'more', labelKey: 'more', icon: DotsThreeOutline },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { app_name, app_tagline, app_logo } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hc_nav_groups')) || {}; } catch { return {}; }
  });

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('hc_nav_groups', JSON.stringify(next));
      return next;
    });
  };

  const navItems = navItemsDef.map(item => {
    const translated = t(item.labelKey);
    return { ...item, label: (translated === item.labelKey && item.defaultLabel) ? item.defaultLabel : translated };
  });
  const itemByPath = Object.fromEntries(navItems.map(i => [i.to, i]));
  const renderNavLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={() => setSidebarOpen(false)}
      data-testid={`nav-${item.labelKey}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200
        ${isActive
          ? 'bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20'
          : 'text-neutral-400 hover:text-white hover:bg-white/5 border border-transparent'}`
      }
    >
      <item.icon size={18} weight="duotone" />
      <span>{item.label}</span>
    </NavLink>
  );
  const mobileNav = mobileNavDef.map(item => {
    const translated = t(item.labelKey);
    return { ...item, label: (translated === item.labelKey && item.defaultLabel) ? item.defaultLabel : translated };
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (user?.temp_password) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="flex min-h-screen bg-[#0D0D0D]" data-testid="app-layout" style={{ background: 'var(--hc-page-bg)' }}>
      <ThemeSyncer />
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4"
        style={{ background: 'rgba(13,13,13,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-xl hover:bg-white/5 transition-colors duration-200" data-testid="mobile-menu-btn">
          {sidebarOpen ? <X size={22} weight="bold" className="text-white" /> : <List size={22} weight="bold" className="text-white" />}
        </button>
        <span className="font-heading font-bold text-white text-base tracking-tight">HomeCommand</span>
        <div className="flex items-center gap-2">
          <ThemeSwitcher compact />
          {/* Quick Actions FAB in header */}
          <button
            data-testid="quick-actions-fab"
            onClick={() => setShowQuickActions(true)}
            className="w-8 h-8 rounded-full bg-[#FF6B35]/10 flex items-center justify-center"
          >
            <SquaresFour size={16} weight="fill" className="text-[#FF6B35]" />
          </button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85D2C] flex items-center justify-center text-white text-xs font-bold">
            {user?.name?.[0] || 'A'}
          </div>
        </div>
      </div>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 bottom-0 left-0 z-50 w-[220px] flex flex-col transition-transform duration-300 ease-out hc-sidebar
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{ background: 'rgba(13,13,13,0.92)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)', borderRight: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="sidebar">
        
        {/* Logo */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3">
            {app_logo && (
              <img src={app_logo} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
            )}
            <div>
              <h1 className="font-heading font-bold text-white text-lg tracking-tight" data-testid="sidebar-app-name">{app_name}</h1>
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#FF6B35] mt-0.5">{app_tagline}</p>
            </div>
          </div>
        </div>

        {/* Scene shortcuts in sidebar (mobile only - when sidebar is open) */}
        {!isDesktop && sidebarOpen && (
          <div className="px-3 pb-3">
            <SceneShortcuts compact />
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-3 overflow-y-auto" data-testid="sidebar-nav">
          <div className="space-y-0.5">
            {NAV_TOP.map(p => itemByPath[p] && renderNavLink(itemByPath[p]))}
          </div>
          <div className="mt-2 space-y-1">
            {NAV_GROUPS.map(group => {
              const items = group.paths.map(p => itemByPath[p]).filter(Boolean);
              const hasActive = group.paths.some(p => location.pathname.startsWith(p));
              const isOpen = openGroups[group.id] || hasActive;
              return (
                <div key={group.id}>
                  <button onClick={() => toggleGroup(group.id)} data-testid={`nav-group-${group.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-colors
                      ${hasActive ? 'text-[#FF6B35]' : 'text-neutral-500 hover:text-neutral-300'}`}>
                    <span>{group.label}</span>
                    <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && <div className="space-y-0.5 mb-1">{items.map(renderNavLink)}</div>}
                </div>
              );
            })}
          </div>
          <div className="mt-2 space-y-0.5 pb-2">
            {NAV_BOTTOM.map(p => itemByPath[p] && renderNavLink(itemByPath[p]))}
          </div>
        </nav>

        {/* User + Logout */}
        <div className="p-4 border-t border-white/5">
          <div className="hidden md:block mb-3">
            <ThemeSwitcher direction="up" />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85D2C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-[11px] text-neutral-500 truncate">{user?.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-2 text-neutral-500 hover:text-red-400 text-xs font-medium transition-colors duration-200 w-full px-1">
            <SignOut size={14} /> {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-[220px] min-h-screen overflow-x-hidden">
        <div className="pt-16 md:pt-0 pb-24 md:pb-0 px-4 md:px-8 lg:px-10 py-6 md:py-8 lg:py-10 max-w-[1600px] mx-auto overflow-x-hidden">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around py-2"
        style={{ background: 'rgba(13,13,13,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="mobile-bottom-nav">
        {mobileNav.map(item => {
          if (item.id === 'more') {
            return (
              <button key="more" onClick={() => setShowMoreMenu(!showMoreMenu)} data-testid="mobile-nav-more"
                className={`flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-colors duration-200
                ${showMoreMenu ? 'text-[#FF6B35]' : 'text-neutral-500 hover:text-neutral-300'}`}>
                <item.icon size={20} weight="duotone" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          }
          return (
            <NavLink key={item.to} to={item.to} onClick={() => setShowMoreMenu(false)} data-testid={`mobile-nav-${item.labelKey}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-colors duration-200
                ${isActive ? 'text-[#FF6B35]' : 'text-neutral-500 hover:text-neutral-300'}`
              }>
              <item.icon size={20} weight="duotone" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* More Menu (mobile flyout) */}
      {showMoreMenu && !isDesktop && (
        <div className="fixed inset-0 z-30" data-testid="more-menu-overlay">
          <div className="absolute inset-0" onClick={() => setShowMoreMenu(false)} />
          <div className="absolute bottom-16 left-2 right-2 max-h-[60vh] overflow-y-auto rounded-3xl animate-in slide-in-from-bottom-4"
            style={{ background: 'rgba(20,20,20,0.98)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
            <div className="p-3 grid grid-cols-4 gap-1">
              {navItems.filter(n =>
                !['/dashboard', '/control-center', '/room-automation', '/alert-center'].includes(n.to)
              ).map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => { setShowMoreMenu(false); setSidebarOpen(false); }}
                  data-testid={`more-nav-${item.labelKey}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-all
                    ${isActive ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'text-neutral-400 hover:bg-white/5'}`
                  }
                >
                  <item.icon size={20} weight="duotone" />
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions Panel */}
      {showQuickActions && <QuickActionsPanel onClose={() => setShowQuickActions(false)} />}

      {/* Mobile Voice Button */}
      {!isDesktop && <MobileVoiceButton />}
    </div>
  );
};
